import { t } from "@redotech/redo-i18n";
import SwitchHorizontal01Icon from "@redotech/redo-icons/switch-horizontal-01_filled.svg";
import { Step } from "@redotech/redo-return-core-common/return-flow";
import { transformProductFromModel } from "@redotech/redo-return-core-common/return-flow-step-from-model";
import {
  isTransformProductStepValid,
  TransformProductState,
} from "@redotech/redo-return-core-common/return-flow-step-valid";
import { STEP_TYPE_NAMES } from "@redotech/redo-return-core-common/return-flow/step-type-names";
import {
  isTransformProductMapMode,
  TRANSFORM_PRODUCT_MAPPED_PATH_LABEL,
  TRANSFORM_PRODUCT_UNMAPPED_PATH_LABEL,
  TransformProductMode,
} from "@redotech/redo-return-core-common/return-flow/transform-product-mode";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import { produce } from "immer";
import * as React from "react";
import { StepType, StepTypeDetailsProps } from "../step";

export const TRANSFORM_PRODUCT = (
  Details: React.ComponentType<StepTypeDetailsProps<TransformProductState>>,
): StepType<TransformProductState, Step.TransformProduct> => ({
  Details: (props) => React.createElement(Details, props),
  customTitle: (state) => state.customTitle,
  description: (state) =>
    isTransformProductMapMode(state.mode)
      ? t("Map this item to a resale SKU")
      : t("Generate a new product for resale"),
  downstream(state) {
    if (isTransformProductMapMode(state.mode)) {
      return [
        { id: state.nextMapped, label: TRANSFORM_PRODUCT_MAPPED_PATH_LABEL },
        {
          id: state.nextUnmapped,
          label: TRANSFORM_PRODUCT_UNMAPPED_PATH_LABEL,
        },
      ];
    }
    return state.next ? [{ id: state.next }] : [];
  },
  empty: { mode: TransformProductMode.Map, scope: "return_item" },
  fromModel: (model, id) => transformProductFromModel(model, id),
  Icon: SwitchHorizontal01Icon,
  iconColor: "success",
  layout: () => BlockLayout.FULL,
  maxDownstream: (state) => (isTransformProductMapMode(state.mode) ? 2 : 1),
  removeConnection(state, targetStepId) {
    return produce(state, (draft) => {
      if (draft.next === targetStepId) {
        draft.next = undefined;
      }
      if (draft.nextMapped === targetStepId) {
        draft.nextMapped = undefined;
      }
      if (draft.nextUnmapped === targetStepId) {
        draft.nextUnmapped = undefined;
      }
    });
  },
  stepDeleted(state, stepId) {
    return produce(state, (draft) => {
      if (draft.next === stepId) {
        draft.next = undefined;
      }
      if (draft.nextMapped === stepId) {
        draft.nextMapped = undefined;
      }
      if (draft.nextUnmapped === stepId) {
        draft.nextUnmapped = undefined;
      }
    });
  },
  title: STEP_TYPE_NAMES[Step.TRANSFORM_PRODUCT],
  toModel(state, id) {
    const mapped = isTransformProductMapMode(state.mode);
    return {
      type: Step.TRANSFORM_PRODUCT,
      customTitle: state.customTitle,
      mode: state.mode ?? TransformProductMode.Map,
      next: !mapped && state.next ? id(state.next) : null,
      nextMapped: mapped && state.nextMapped ? id(state.nextMapped) : null,
      nextUnmapped:
        mapped && state.nextUnmapped ? id(state.nextUnmapped) : null,
      scope: state.scope,
    };
  },
  updateConnection(state, handleIndex, targetStepId) {
    return produce(state, (draft) => {
      if (isTransformProductMapMode(draft.mode)) {
        if (handleIndex === 0) {
          draft.nextMapped = targetStepId;
        } else if (handleIndex === 1) {
          draft.nextUnmapped = targetStepId;
        } else {
          throw new Error(
            `Invalid handle index ${handleIndex} for transform product step`,
          );
        }
        return;
      }
      if (handleIndex !== 0) {
        throw new Error(
          `Invalid handle index ${handleIndex} for transform product step`,
        );
      }
      draft.next = targetStepId;
    });
  },
  valid: (state) => isTransformProductStepValid(state).valid,
});
